const STORAGE_KEY = 'ubbi_schedule_workspace_prefs';

const DEFAULT_PREFS = {
  range: 'day',
  mode: 'agenda',
  viewMode: 'classic',
  groupBy: 'professional',
  columnOrder: {
    professional: [],
    specialty: [],
  },
};

function storageKey({ userId, placeId } = {}) {
  if (placeId && userId) return `${STORAGE_KEY}:${placeId}:${userId}`;
  if (userId) return `${STORAGE_KEY}:${userId}`;
  return STORAGE_KEY;
}

export function normalizeIdList(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const ids = [];
  for (const value of list) {
    if (value == null || value === '') continue;
    const id = Number(value);
    if (!Number.isFinite(id) || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

function normalizeColumnOrder(parsed) {
  const source = parsed?.columnOrder && typeof parsed.columnOrder === 'object'
    ? parsed.columnOrder
    : {};
  return {
    professional: normalizeIdList(source.professional),
    specialty: normalizeIdList(source.specialty),
  };
}

function normalizePrefs(parsed) {
  return {
    range: ['day', 'week', 'month'].includes(parsed?.range) ? parsed.range : DEFAULT_PREFS.range,
    mode: ['agenda', 'reception'].includes(parsed?.mode) ? parsed.mode : DEFAULT_PREFS.mode,
    viewMode: ['classic', 'board'].includes(parsed?.viewMode) ? parsed.viewMode : DEFAULT_PREFS.viewMode,
    groupBy: ['professional', 'specialty'].includes(parsed?.groupBy) ? parsed.groupBy : DEFAULT_PREFS.groupBy,
    columnOrder: normalizeColumnOrder(parsed),
  };
}

function readKey(key) {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  return normalizePrefs(JSON.parse(raw));
}

export function loadScheduleWorkspacePrefs(scope = {}) {
  try {
    return readKey(storageKey(scope))
      || readKey(STORAGE_KEY)
      || { ...DEFAULT_PREFS, columnOrder: { professional: [], specialty: [] } };
  } catch {
    return { ...DEFAULT_PREFS, columnOrder: { professional: [], specialty: [] } };
  }
}

export function saveScheduleWorkspacePrefs(prefs, scope = {}) {
  try {
    const current = loadScheduleWorkspacePrefs(scope);
    const next = normalizePrefs({
      ...current,
      ...prefs,
      columnOrder: {
        ...current.columnOrder,
        ...(prefs?.columnOrder || {}),
      },
    });
    localStorage.setItem(storageKey(scope), JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Applies a saved id order to the current API list.
 * - Known ids keep the saved sequence.
 * - Ids absent from the response are skipped (kept only in prefs).
 * - New ids from the API are appended in the response order.
 */
export function sortItemsByIdOrder(items, order, idKey) {
  const list = Array.isArray(items) ? [...items] : [];
  const ids = normalizeIdList(order);
  if (!ids.length || !idKey) return list;

  const byId = new Map();
  for (const item of list) {
    byId.set(Number(item?.[idKey]), item);
  }

  const ordered = [];
  for (const id of ids) {
    const item = byId.get(id);
    if (!item) continue;
    ordered.push(item);
    byId.delete(id);
  }

  for (const item of list) {
    const id = Number(item?.[idKey]);
    if (byId.has(id)) {
      ordered.push(item);
      byId.delete(id);
    }
  }

  return ordered;
}

/**
 * Merges a drag result (current visible ids) with the previous saved order.
 * Absent ids stay in their relative slots so they reclaim position when they return.
 * Brand-new ids are appended.
 *
 * Example: saved [1,2,3,4,5], visible dragged to [5,1,3] → [5,2,1,4,3]
 */
export function mergeColumnOrder(previousIds, currentOrderedIds) {
  const current = normalizeIdList(currentOrderedIds);
  const previous = normalizeIdList(previousIds);
  if (!previous.length) return current;
  if (!current.length) return previous;

  const currentSet = new Set(current);
  const result = [];
  let currentIdx = 0;

  for (const id of previous) {
    if (currentSet.has(id)) {
      if (currentIdx < current.length) {
        result.push(current[currentIdx]);
        currentIdx += 1;
      }
      continue;
    }
    result.push(id);
  }

  while (currentIdx < current.length) {
    result.push(current[currentIdx]);
    currentIdx += 1;
  }

  return normalizeIdList(result);
}
